<template>
  <!-- «طلبات الاستئذان» / «طلبات الزيارة» from the parent app (route prop `type`: 'leave' | 'visit').
       Server: /user/students/requests/* (PARENT_REQUESTS_SPEC.md A+B); helpers in src/libs/parent-requests.js. -->
  <div class="prq" :class="'prq-' + type">
    <div class="prq-head">
      <div class="prq-head-icon"><i class="fa" :class="T.icon"></i></div>
      <div class="prq-head-text">
        <h4>
          {{ T.title }}
          <span class="prq-live" v-if="countOf('pending') > 0"><i class="fa fa-circle"></i> {{ countOf('pending') }} بانتظار الإجراء</span>
        </h4>
        <p>{{ T.hint }}</p>
      </div>
      <div class="prq-head-actions">
        <button type="button" class="btn btn-outline-secondary" @click="load()" :disabled="loading">
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> تحديث
        </button>
        <button type="button" class="btn btn-outline-secondary prq-filter-btn" :class="{ on: showFilters || filtered }" @click="showFilters = !showFilters" :aria-expanded="showFilters">
          <i class="fa fa-filter"></i> فلترة<template v-if="filtered"> (مفعّلة)</template>
        </button>
        <a v-if="checkPer('app-settings')" href="/app-settings#parent-requests" class="btn btn-outline-primary">
          <i class="fa fa-cog"></i> إعدادات الطلبات
        </a>
      </div>
    </div>

    <div class="prq-note off" v-if="settingsLoaded && !conf.enabled">
      <i class="fa fa-pause-circle"></i>
      <span>{{ type == 'leave' ? 'استقبال طلبات الاستئذان موقوف' : 'استقبال طلبات الزيارة موقوف' }} في تطبيق ولي الأمر. يمكنك تفعيله من «إعدادات الطلبات».</span>
    </div>

    <!-- server warnings[] of the last action (e.g. no calls service) -->
    <div class="prq-note warn" v-if="warnings.length">
      <i class="fa fa-exclamation-triangle"></i>
      <div class="prq-note-body">
        <b>تنبيه</b>
        <ul><li v-for="(w, i) in warnings" :key="i">{{ w }}</li></ul>
      </div>
      <button type="button" class="close" aria-label="إغلاق" @click="warnings = []">&times;</button>
    </div>

    <div class="prq-tabs" role="tablist">
      <button v-for="t in tabs" :key="t.key" type="button" role="tab" class="prq-tab" :class="['t-' + t.key, { on: tab == t.key }]" :aria-selected="tab == t.key" @click="setTab(t.key)">
        <i class="fa" :class="t.icon"></i> {{ t.title }}
        <span class="n" v-if="countOf(t.key) !== ''">{{ countOf(t.key) }}</span>
      </button>
    </div>

    <div class="prq-filters" :class="{ 'prq-filters-closed': !showFilters }">
      <div class="f-dates">
        <div class="f-field">
          <label for="prq-from">من تاريخ</label>
          <input id="prq-from" type="date" class="form-control" v-model="from" @change="load()">
        </div>
        <div class="f-field">
          <label for="prq-to">إلى تاريخ</label>
          <input id="prq-to" type="date" class="form-control" v-model="to" @change="load()">
        </div>
        <div class="f-quick">
          <button type="button" class="chip" :class="{ on: quick == 'today' }" @click="setQuick('today')">اليوم</button>
          <button type="button" class="chip" :class="{ on: quick == 'tomorrow' }" @click="setQuick('tomorrow')">غداً</button>
          <button type="button" class="chip" :class="{ on: quick == 'week' }" @click="setQuick('week')">الأسبوع القادم</button>
          <button type="button" class="chip" :class="{ on: quick == '' }" @click="setQuick('')">كل التواريخ</button>
        </div>
      </div>
      <div class="f-row">
        <div class="f-field">
          <label for="prq-classname">الصف</label>
          <select id="prq-classname" class="form-control" v-model="classname" @change="classroom = ''">
            <option value="">كل الصفوف</option>
            <option v-for="c in classes" :key="c.title" :value="c.title">{{ c.title }}</option>
          </select>
        </div>
        <div class="f-field">
          <label for="prq-classroom">الفصل</label>
          <select id="prq-classroom" class="form-control" v-model="classroom" :disabled="!classname">
            <option value="">{{ classname ? 'كل الفصول' : '-- اختر الصف أولاً --' }}</option>
            <option v-for="c in classrooms" :key="c" :value="c">{{ c }}</option>
          </select>
        </div>
        <div class="f-field grow">
          <label for="prq-q">بحث</label>
          <input id="prq-q" type="search" class="form-control" v-model="q" placeholder="اسم الطالب أو رقم الهوية أو اسم ولي الأمر أو جواله..." @keyup.enter="load()">
        </div>
      </div>
    </div>

    <div class="text-center g" v-if="loading && !loaded">
      <img :src="require('@/assets/images/loading.svg')" alt="" width="50">
    </div>

    <div class="prq-empty" v-else-if="!shown.length">
      <i class="fa" :class="tab == 'pending' ? 'fa-check-circle' : 'fa-inbox'"></i>
      <b>{{ tab == 'pending' ? 'لا توجد طلبات بانتظار الإجراء' : 'لا توجد طلبات' }}</b>
      <small>{{ filtered ? 'جرّب تغيير الفلاتر.' : (type == 'leave' ? 'تظهر هنا طلبات الاستئذان التي يرسلها أولياء الأمور من التطبيق.' : 'تظهر هنا طلبات الزيارة التي يرسلها أولياء الأمور من التطبيق.') }}</small>
    </div>

    <div class="prq-list" v-else>
      <article v-for="r in shown" :key="r._id" class="prq-card" :class="['st-' + r.status, { busy: busyId == r._id }]">
        <header>
          <div class="prq-avatar">{{ initial(r.student_name) }}</div>
          <div class="prq-who">
            <h5>{{ r.student_name || '—' }}</h5>
            <small>{{ classText(r) }}<template v-if="r.number"> · <span class="ltr-num">{{ r.number }}</span></template></small>
          </div>
          <span class="prq-chip" :class="'c-' + r.status">{{ statusTitle(r) }}</span>
        </header>

        <div class="prq-body">
          <div class="prq-when">
            <span class="w-date"><i class="fa fa-calendar"></i> {{ dayName(r.date) }} <b class="ltr-num">{{ r.date }}</b></span>
            <span class="w-rel" v-if="rel(r.date)" :class="{ today: rel(r.date) == 'اليوم' }">{{ rel(r.date) }}</span>
            <span class="w-time" v-if="r.time"><i class="fa fa-clock-o"></i> {{ type == 'leave' ? 'وقت الخروج' : 'الساعة' }} <b>{{ time12(r.time) }}</b></span>
          </div>

          <div class="prq-reason">
            <span class="lbl">{{ type == 'leave' ? 'السبب' : 'الغرض من الزيارة' }}</span>
            <p>{{ r.reason || '—' }}</p>
          </div>

          <div class="prq-meta">
            <span v-if="type == 'leave' && r.pickup && r.pickup.name"><i class="fa fa-car"></i> يستلمه: <b>{{ r.pickup.name }}</b><template v-if="r.pickup.relation"> ({{ r.pickup.relation }})</template></span>
            <span><i class="fa fa-user"></i> {{ parentOf(r).name || 'ولي الأمر' }}<template v-if="parentOf(r).relation"> ({{ parentOf(r).relation }})</template></span>
            <a v-if="parentOf(r).phone" :href="'tel:' + parentOf(r).phone" class="ltr-num"><i class="fa fa-phone"></i> {{ parentOf(r).phone }}</a>
            <span class="muted" :title="fmt(r.created_at)"><i class="fa fa-paper-plane-o"></i> أُرسل {{ ago(r.created_at) }}</span>
          </div>

          <div class="prq-proposed" v-if="r.proposed && r.proposed.date">
            <i class="fa fa-calendar-plus-o"></i>
            الموعد المقترح من المدرسة: <b>{{ dayName(r.proposed.date) }} <span class="ltr-num">{{ r.proposed.date }}</span><template v-if="r.proposed.time"> — {{ time12(r.proposed.time) }}</template></b>
          </div>

          <div class="prq-reply" v-if="r.reply">
            <i class="fa fa-reply"></i> <span>رد المدرسة: {{ r.reply }}</span>
          </div>

          <div class="prq-done" v-if="doneActions(r).length">
            <span v-for="a in doneActions(r)" :key="a" class="done-chip"><i class="fa fa-check"></i> {{ doneTitle(a) }}</span>
          </div>
        </div>

        <footer>
          <template v-if="isOpen(r)">
            <button type="button" class="btn btn-sm btn-success" @click="openApprove(r)"><i class="fa fa-check"></i> موافقة</button>
            <button type="button" class="btn btn-sm btn-outline-danger" @click="openReject(r)"><i class="fa fa-times"></i> رفض</button>
          </template>
          <template v-if="isApproved(r)">
            <template v-if="type == 'leave'">
              <button type="button" class="btn btn-sm btn-primary" @click="act(r, 'call')"><i class="fa fa-bullhorn"></i> مناداة الآن</button>
              <button type="button" class="btn btn-sm" :class="hasDone(r, 'leave_record') ? 'btn-outline-secondary' : 'btn-outline-primary'" :disabled="hasDone(r, 'leave_record')" @click="act(r, 'leave_record')">
                <i class="fa fa-sign-out"></i> {{ hasDone(r, 'leave_record') ? 'سُجّل الخروج' : 'تسجيل الخروج الآن' }}
              </button>
              <button type="button" class="btn btn-sm btn-outline-primary" v-if="!hasDone(r, 'teachers')" @click="act(r, 'teachers')"><i class="fa fa-users"></i> إبلاغ المعلمين</button>
              <button type="button" class="btn btn-sm btn-outline-success" @click="act(r, 'done')"><i class="fa fa-flag-checkered"></i> إنهاء الطلب</button>
            </template>
            <template v-else>
              <button type="button" class="btn btn-sm btn-primary" @click="act(r, 'done')"><i class="fa fa-check-square-o"></i> تمت الزيارة</button>
              <button type="button" class="btn btn-sm btn-outline-primary" v-if="!hasDone(r, 'visit_record')" @click="act(r, 'visit_record')"><i class="fa fa-book"></i> إضافة إلى سجل الزيارات</button>
            </template>
          </template>
          <button type="button" class="btn btn-sm btn-outline-info" v-if="r.status != 'cancelled'" @click="openContact(r)"><i class="fa fa-comments-o"></i> تواصل</button>
          <button type="button" class="btn btn-sm btn-flat-secondary prq-hist-btn" @click="toggleHistory(r)" :aria-expanded="!!openHistory[r._id]">
            <i class="fa fa-history"></i> السجل ({{ (r.history || []).length }})
            <i class="fa" :class="openHistory[r._id] ? 'fa-angle-up' : 'fa-angle-down'"></i>
          </button>
        </footer>

        <div class="prq-history" v-if="openHistory[r._id]">
          <ol>
            <li v-for="(h, i) in historyOf(r)" :key="i" :class="'by-' + (h.by || 'school')">
              <span class="dot"></span>
              <div>
                <b>{{ actionTitle(h) }}</b>
                <small>{{ h.by == 'parent' ? 'ولي الأمر' : (h.by_name || 'المدرسة') }} — {{ fmt(h.at) }}</small>
                <p v-if="h.note">{{ h.note }}</p>
              </div>
            </li>
            <li v-if="!historyOf(r).length" class="muted">لا يوجد سجل.</li>
          </ol>
        </div>
      </article>
    </div>

    <!-- موافقة -->
    <b-modal id="prq-approve" :title="type == 'leave' ? 'الموافقة على طلب الاستئذان' : 'الموافقة على طلب الزيارة'" hide-footer centered no-close-on-backdrop>
      <div v-if="cur">
        <div class="prq-sum">
          <b>{{ cur.student_name }}</b> <small>{{ classText(cur) }}</small>
          <div><i class="fa fa-calendar"></i> {{ dayName(cur.date) }} <span class="ltr-num">{{ cur.date }}</span><template v-if="cur.time"> — {{ time12(cur.time) }}</template></div>
          <div class="muted">{{ cur.reason }}</div>
        </div>

        <div class="form-group">
          <label for="prq-approve-reply">رد المدرسة لولي الأمر <small class="text-muted">(يصله مع إشعار الموافقة — اختياري)</small></label>
          <textarea id="prq-approve-reply" class="form-control" rows="2" maxlength="500" v-model="form.reply" placeholder="مثال: تمت الموافقة، يرجى الحضور إلى البوابة الرئيسية."></textarea>
        </div>

        <template v-if="type == 'leave'">
          <div class="prq-sub">عند الموافقة</div>
          <label class="prq-opt" :class="{ on: form.actions.call }">
            <input type="checkbox" v-model="form.actions.call">
            <span><b>مناداة الطالب الآن في نظام المناداة</b><small>يظهر اسمه في شاشة المناداة ليخرج إلى ولي أمره.</small></span>
          </label>
          <label class="prq-opt" :class="{ on: form.actions.leave_record }">
            <input type="checkbox" v-model="form.actions.leave_record">
            <span><b>تسجيل استئذان (انصراف) الآن</b><small>يُسجَّل مستأذناً في تقرير اليوم، ويصل ولي الأمر إشعار الاستئذان المعتاد.</small></span>
          </label>
          <label class="prq-opt" :class="{ on: form.actions.teachers }">
            <input type="checkbox" v-model="form.actions.teachers">
            <span><b>إبلاغ معلمي الطالب</b><small>يصل معلميه «إذن خروج من المدرسة» في تطبيق المعلم.</small></span>
          </label>
          <small class="text-muted d-block">يمكنك تنفيذ أي خيار لاحقاً من بطاقة الطلب، مثل «مناداة الآن» عند وصول ولي الأمر.</small>
        </template>
        <template v-else>
          <label class="prq-opt" :class="{ on: form.actions.visit_record }">
            <input type="checkbox" v-model="form.actions.visit_record">
            <span><b>إضافة إلى سجل زيارات أولياء الأمور</b><small>تظهر في «زيارات اولياء الامور» بالتاريخ والوقت والغرض.</small></span>
          </label>
          <label class="prq-opt" :class="{ on: form.propose }">
            <input type="checkbox" v-model="form.propose">
            <span><b>اقتراح موعد آخر</b><small>يصل ولي الأمر الموعد الذي تقترحه المدرسة بدل موعده.</small></span>
          </label>
          <div class="prq-propose" v-if="form.propose">
            <div class="f-field">
              <label for="prq-p-date">التاريخ المقترح</label>
              <input id="prq-p-date" type="date" class="form-control" v-model="form.proposed.date" :min="today">
            </div>
            <div class="f-field">
              <label for="prq-p-time">الوقت المقترح</label>
              <input id="prq-p-time" type="time" class="form-control" v-model="form.proposed.time">
            </div>
            <small class="text-muted" v-if="form.proposed.date">{{ dayName(form.proposed.date) }}<template v-if="form.proposed.time"> — {{ time12(form.proposed.time) }}</template></small>
          </div>
        </template>

        <div class="prq-modal-actions">
          <button type="button" class="btn btn-outline-secondary" @click="$bvModal.hide('prq-approve')">إلغاء</button>
          <button type="button" class="btn btn-success" :disabled="sending" @click="approve()">
            <i class="fa" :class="sending ? 'fa-spinner fa-spin' : 'fa-check'"></i> تأكيد الموافقة
          </button>
        </div>
      </div>
    </b-modal>

    <!-- رفض -->
    <b-modal id="prq-reject" :title="type == 'leave' ? 'رفض طلب الاستئذان' : 'رفض طلب الزيارة'" hide-footer centered no-close-on-backdrop>
      <div v-if="cur">
        <div class="prq-sum">
          <b>{{ cur.student_name }}</b> <small>{{ classText(cur) }}</small>
          <div><i class="fa fa-calendar"></i> {{ dayName(cur.date) }} <span class="ltr-num">{{ cur.date }}</span><template v-if="cur.time"> — {{ time12(cur.time) }}</template></div>
        </div>
        <div class="form-group">
          <label for="prq-reject-reply">سبب الرفض <small class="text-muted">(يصل لولي الأمر)</small></label>
          <textarea id="prq-reject-reply" class="form-control" rows="3" maxlength="500" v-model="form.reply"></textarea>
        </div>
        <div class="f-quick mb-1">
          <button v-for="t in T.rejects" :key="t" type="button" class="chip" @click="form.reply = t">{{ t }}</button>
        </div>
        <div class="prq-modal-actions">
          <button type="button" class="btn btn-outline-secondary" @click="$bvModal.hide('prq-reject')">إلغاء</button>
          <button type="button" class="btn btn-danger" :disabled="sending" @click="reject()">
            <i class="fa" :class="sending ? 'fa-spinner fa-spin' : 'fa-times'"></i> تأكيد الرفض
          </button>
        </div>
      </div>
    </b-modal>

    <!-- تواصل -->
    <b-modal id="prq-contact" title="التواصل مع ولي الأمر" hide-footer centered>
      <div v-if="cur">
        <div class="prq-sum">
          <b>{{ parentOf(cur).name || 'ولي الأمر' }}</b> <small v-if="parentOf(cur).relation">({{ parentOf(cur).relation }})</small>
          <div>ولي أمر الطالب {{ cur.student_name }} — {{ classText(cur) }}</div>
          <div class="ltr-num" v-if="parentOf(cur).phone"><i class="fa fa-phone"></i> {{ parentOf(cur).phone }}</div>
          <div class="text-danger" v-else>لا يوجد رقم جوال في الطلب.</div>
        </div>

        <div class="prq-contact">
          <a class="prq-ct call" :class="{ disabled: !parentOf(cur).phone }" :href="parentOf(cur).phone ? 'tel:' + parentOf(cur).phone : null" @click="contacted('call')">
            <i class="fa fa-phone"></i><b>اتصال</b><small class="ltr-num">{{ parentOf(cur).phone || '—' }}</small>
          </a>
          <a class="prq-ct wa" :class="{ disabled: !waLink }" :href="waLink || null" target="_blank" rel="noopener" @click="contacted('whatsapp', contactForm.message)">
            <i class="fa fa-whatsapp"></i><b>واتساب</b><small>فتح محادثة بالنص أدناه</small>
          </a>
        </div>

        <div class="form-group mt-1">
          <label for="prq-msg">نص الرسالة</label>
          <textarea id="prq-msg" class="form-control" rows="4" v-model="contactForm.message"></textarea>
        </div>
        <div class="prq-sub">رسالة عبر</div>
        <div class="prq-send">
          <button type="button" class="btn btn-sm btn-outline-success" @click="sendMsg('whatsapp')"><i class="fa fa-whatsapp"></i> واتس ذكي</button>
          <button type="button" class="btn btn-sm btn-outline-success" @click="sendMsg('xwa')"><i class="fa fa-whatsapp"></i> واتس مباشر</button>
          <button type="button" class="btn btn-sm btn-outline-success" @click="sendMsg('wa')"><i class="fa fa-chrome"></i> اداة المتصفح</button>
          <meta-send-button size="sm" :block="false" @send="sendMsg('meta')" />
          <button type="button" class="btn btn-sm btn-outline-primary" @click="sendMsg('sms')"><i class="fa fa-comment"></i> رسالة نصية</button>
          <button type="button" class="btn btn-sm btn-outline-primary" @click="sendMsg('push')"><i class="fa fa-bell"></i> إشعار الجوال</button>
        </div>

        <div class="prq-sub">أو سجّل تواصلاً تم بطريقة أخرى</div>
        <div class="prq-other">
          <input type="text" class="form-control" v-model="contactForm.note" maxlength="500" placeholder="مثال: تم الاتصال وسيحضر الساعة 10 صباحاً">
          <button type="button" class="btn btn-outline-secondary" :disabled="sending || !contactForm.note.trim()" @click="contacted('other', contactForm.note)">تسجيل</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal, VBModal } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
  REQ_PERS, reqCounts, reqPost, pick, errorText, withDefaults, settingsOf, refreshRequestCounts,
} from "@/libs/parent-requests";

const TYPES = {
  leave: {
    title: 'طلبات الاستئذان',
    hint: 'طلبات خروج الطلاب التي يرسلها أولياء الأمور من التطبيق: وافق مع مناداة الطالب أو تسجيل استئذانه، أو ارفض، أو تواصل مع ولي الأمر.',
    icon: 'fa-sign-out',
    noun: 'طلب الاستئذان',
    rejects: ['لا يمكن الاستئذان في هذا الوقت', 'يرجى مراجعة وكيل شؤون الطلاب', 'يوجد اختبار في هذا اليوم'],
  },
  visit: {
    title: 'طلبات الزيارة',
    hint: 'مواعيد الزيارة التي يطلبها أولياء الأمور من التطبيق: وافق وأضفها لسجل الزيارات، أو اقترح موعداً آخر، أو ارفض، أو تواصل مع ولي الأمر.',
    icon: 'fa-calendar-check-o',
    noun: 'طلب الزيارة',
    rejects: ['الموعد غير مناسب، نأمل اختيار موعد آخر', 'يرجى التواصل مع المدرسة لتحديد موعد', 'المعني بالزيارة غير متواجد في هذا اليوم'],
  },
};
const DAYS = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const STATUS = ['pending', 'approved', 'contacted', 'done', 'rejected', 'cancelled'];

function riyadhDate(d) {
  try {
    return (d || new Date()).toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });
  } catch (e) {
    return (d || new Date()).toISOString().split('T')[0];
  }
}
function addDays(date, n) {
  var d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().split('T')[0];
}

export default {
  components: { BModal },
  directives: { "b-modal": VBModal },
  props: {
    type: { type: String, default: 'leave' },
  },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user')),
      live: reqCounts,
      rows: [],
      counts: {},
      tab: 'pending',
      from: '',
      to: '',
      quick: '',
      classname: '',
      classroom: '',
      q: '',
      classes: [],
      loading: false,
      loaded: false,
      settings: withDefaults(null),
      settingsLoaded: false,
      warnings: [],
      openHistory: {},
      cur: null,
      busyId: '',
      sending: false,
      form: { reply: '', actions: {}, propose: false, proposed: { date: '', time: '' } },
      contactForm: { message: '', note: '' },
      // phones: the filters open on demand
      showFilters: false,
      today: riyadhDate(),
      seq: 0,
    };
  },
  computed: {
    T() {
      return TYPES[this.type] || TYPES.leave;
    },
    conf() {
      return this.settings[this.type] || {};
    },
    tabs() {
      return [
        { key: 'pending', title: 'قيد الانتظار', icon: 'fa-hourglass-half' },
        { key: 'approved', title: 'تمت الموافقة', icon: 'fa-check-circle' },
        { key: 'contacted', title: 'تم التواصل', icon: 'fa-comments-o' },
        { key: 'done', title: this.type == 'leave' ? 'منتهية' : 'تمت الزيارة', icon: 'fa-flag-checkered' },
        { key: 'rejected', title: 'مرفوضة', icon: 'fa-times-circle' },
        { key: 'cancelled', title: 'ألغاها ولي الأمر', icon: 'fa-ban' },
        { key: 'all', title: 'الكل', icon: 'fa-list' },
      ];
    },
    classrooms() {
      var c = this.classname;
      var g = this.classes.find(function (x) { return x.title == c; });
      return g ? g.classrooms : [];
    },
    filtered() {
      return !!(this.from || this.to || this.classname || this.q.trim());
    },
    // the rows of this tab (the server filters by status; checked again here) + class / search
    shown() {
      var g = this, q = this.q.trim();
      var out = this.rows.filter(function (r) {
        if (g.tab != 'all' && r.status != g.tab) return false;
        if (g.classname && r.classname != g.classname) return false;
        if (g.classroom && String(r.classroom) != String(g.classroom)) return false;
        if (q) {
          var p = g.parentOf(r);
          var hay = [r.student_name, r.number, p.name, p.phone, r.reason].join(' ');
          if (hay.indexOf(q) === -1) return false;
        }
        return true;
      });
      // what needs an action first: the nearest day / time; the rest newest first
      if (this.tab == 'pending') {
        out.sort(function (a, b) {
          return String(a.date || '').localeCompare(String(b.date || '')) || String(a.time || '99').localeCompare(String(b.time || '99'));
        });
      } else {
        out.sort(function (a, b) {
          return String(b.created_at || '').localeCompare(String(a.created_at || ''));
        });
      }
      return out;
    },
    waLink() {
      var p = this.cur ? this.intl(this.parentOf(this.cur).phone) : '';
      if (!p) return '';
      return 'https://wa.me/' + p + (this.contactForm.message ? '?text=' + encodeURIComponent(this.contactForm.message) : '');
    },
  },
  watch: {
    type() {
      this.rows = [];
      this.counts = {};
      this.tab = 'pending';
      this.warnings = [];
      this.loaded = false;
      if (this.allowed()) this.load();
    },
    // a new request (or one cancelled) while the page is open: the list follows the sidebar count
    'live.tick'() {
      this.load(true);
    },
  },
  created() {
    if (!this.allowed()) {
      this.$router.push('/NotPermitted');
      return;
    }
    this.loadClasses();
    this.loadSettings();
    this.load();
  },
  methods: {
    allowed() {
      return checkPer(REQ_PERS[this.type]);
    },
    // ------------------------------------------------------------ data
    load(quiet) {
      var g = this, seq = ++g.seq;
      if (!quiet) g.loading = true;
      reqPost('/list', {
        type: g.type,
        status: g.tab == 'all' ? '' : g.tab,
        from: g.from,
        to: g.to,
        q: g.q.trim(),
      }).then(function (r) {
        if (seq != g.seq) return;
        g.loading = false;
        g.loaded = true;
        if (!r || r.status != 100) {
          if (!quiet) alert(errorText(r), 200);
          return;
        }
        var rows = Array.isArray(r.response) ? r.response : (pick(r, 'rows') || pick(r, 'list') || []);
        g.rows = Array.isArray(rows) ? rows.filter(function (x) { return x && (!x.type || x.type == g.type); }) : [];
        var c = pick(r, 'counts');
        if (c && typeof c == 'object' && c[g.type] && typeof c[g.type] == 'object') c = c[g.type];
        g.counts = c && typeof c == 'object' ? c : {};
        var s = pick(r, 'settings');
        if (s && typeof s == 'object' && (s.leave || s.visit)) {
          g.settings = withDefaults(s);
          g.settingsLoaded = true;
        }
      }).catch(function () {
        if (seq != g.seq) return;
        g.loading = false;
        g.loaded = true;
        if (!quiet) alert('تعذّر الاتصال بالخادم', 200);
      });
    },
    loadSettings() {
      var g = this;
      reqPost('/settings', {}, { global: false }).then(function (r) {
        var s = r && r.status == 100 ? settingsOf(r) : null;
        if (s) g.settings = withDefaults(s);
        if (r && r.status == 100) g.settingsLoaded = true;
      }).catch(function () {});
    },
    loadClasses() {
      var g = this;
      $.post(api + '/user/students/classes', { jwt: g.user.jwt }).then(function (r) {
        if (r.status == 100 && Array.isArray(r.response)) g.classes = r.response;
      });
    },
    setTab(t) {
      this.tab = t;
      this.load();
    },
    setQuick(k) {
      this.quick = k;
      if (k == 'today') { this.from = this.today; this.to = this.today; }
      else if (k == 'tomorrow') { this.from = addDays(this.today, 1); this.to = this.from; }
      else if (k == 'week') { this.from = this.today; this.to = addDays(this.today, 7); }
      else { this.from = ''; this.to = ''; }
      this.load();
    },
    countOf(k) {
      var c = this.counts || {};
      if (k == 'all') {
        if (c.all !== undefined) return c.all;
        if (c.total !== undefined) return c.total;
        var known = STATUS.filter(function (s) { return c[s] !== undefined; });
        if (known.length) return known.reduce(function (a, s) { return a + (parseInt(c[s], 10) || 0); }, 0);
        return this.tab == 'all' ? this.rows.length : '';
      }
      if (c[k] !== undefined) return parseInt(c[k], 10) || 0;
      if (k == 'pending' && this.live.loaded) return this.live[this.type];
      if (this.tab == k || this.tab == 'all') return this.rows.filter(function (r) { return r.status == k; }).length;
      return '';
    },
    // ------------------------------------------------------------ display
    parentOf(r) {
      var p = (r && r.parent) || {};
      return { name: p.name || r.parent_name || '', phone: p.phone || r.parent_phone || '', relation: p.relation || r.relation || '' };
    },
    classText(r) {
      return [r.classname, r.classroom].filter(function (x) { return x !== undefined && x !== null && x !== ''; }).join(' - ') || '—';
    },
    initial(n) {
      return (n || '؟').trim().charAt(0);
    },
    statusTitle(r) {
      var done = this.type == 'leave' ? 'منتهي' : 'تمت الزيارة';
      return { pending: 'قيد الانتظار', approved: 'تمت الموافقة', rejected: 'مرفوض', contacted: 'تم التواصل', done: done, cancelled: 'ألغاه ولي الأمر' }[r.status] || r.status;
    },
    isApproved(r) {
      if (r.status == 'approved') return true;
      if (r.status != 'contacted') return false;
      return (r.history || []).some(function (h) { return h.action == 'approve' || h.action == 'approved'; });
    },
    isOpen(r) {
      return r.status == 'pending' || (r.status == 'contacted' && !this.isApproved(r));
    },
    doneActions(r) {
      return Array.isArray(r.done_actions) ? r.done_actions : [];
    },
    hasDone(r, a) {
      return this.doneActions(r).indexOf(a) > -1;
    },
    doneTitle(a) {
      return { call: 'تمت المناداة', leave_record: 'سُجّل الاستئذان', teachers: 'أُبلغ المعلمون', visit_record: 'أضيفت لسجل الزيارات' }[a] || a;
    },
    historyOf(r) {
      return (r.history || []).slice().sort(function (a, b) { return String(a.at || '').localeCompare(String(b.at || '')); });
    },
    actionTitle(h) {
      var m = {
        create: 'أرسل ولي الأمر الطلب', created: 'أرسل ولي الأمر الطلب', cancel: 'ألغى ولي الأمر الطلب', cancelled: 'ألغى ولي الأمر الطلب',
        approve: 'تمت الموافقة', approved: 'تمت الموافقة', reject: 'تم الرفض', rejected: 'تم الرفض',
        contact: 'تواصلت المدرسة مع ولي الأمر', contacted: 'تواصلت المدرسة مع ولي الأمر',
        call: 'مناداة الطالب في نظام المناداة', leave_record: 'تسجيل استئذان (انصراف)', teachers: 'إبلاغ معلمي الطالب',
        visit_record: 'إضافة إلى سجل الزيارات', propose: 'اقتراح موعد آخر', proposed: 'اقتراح موعد آخر',
        done: this.type == 'leave' ? 'إنهاء الطلب' : 'تمت الزيارة',
      };
      var t = m[h.action] || h.action || '—';
      var how = { call: 'اتصال', whatsapp: 'واتساب', message: 'رسالة', other: 'طريقة أخرى' }[h.method];
      return how && (h.action == 'contact' || h.action == 'contacted') ? t + ' (' + how + ')' : t;
    },
    dayName(date) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return '';
      return DAYS[new Date(date + 'T12:00:00Z').getUTCDay()];
    },
    rel(date) {
      if (date == this.today) return 'اليوم';
      if (date == addDays(this.today, 1)) return 'غداً';
      if (date == addDays(this.today, -1)) return 'أمس';
      return '';
    },
    time12(t) {
      var m = /^(\d{1,2}):(\d{2})/.exec(t || '');
      if (!m) return t || '';
      var h = parseInt(m[1], 10);
      return (h % 12 || 12) + ':' + m[2] + (h < 12 ? ' ص' : ' م');
    },
    fmt(at) {
      if (!at) return '';
      var d = new Date(at);
      if (isNaN(d.getTime())) return String(at);
      try {
        var s = d.toLocaleString('en-CA', { timeZone: 'Asia/Riyadh', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        var p = s.split(', ');
        return p[0] + ' ' + this.time12((p[1] || '').replace(/^24/, '00'));
      } catch (e) {
        return d.toISOString().replace('T', ' ').slice(0, 16);
      }
    },
    ago(at) {
      var d = new Date(at);
      if (!at || isNaN(d.getTime())) return '';
      var s = Math.max(0, (Date.now() - d.getTime()) / 1000);
      if (s < 60) return 'الآن';
      var m = Math.floor(s / 60);
      if (m < 60) return 'منذ ' + (m == 1 ? 'دقيقة' : m == 2 ? 'دقيقتين' : m + (m <= 10 ? ' دقائق' : ' دقيقة'));
      var h = Math.floor(m / 60);
      if (h < 24) return 'منذ ' + (h == 1 ? 'ساعة' : h == 2 ? 'ساعتين' : h + (h <= 10 ? ' ساعات' : ' ساعة'));
      return this.fmt(at);
    },
    // Saudi numbers → wa.me form (9665…)
    intl(phone) {
      var d = String(phone || '').replace(/\D/g, '');
      if (!d) return '';
      if (d.indexOf('00') === 0) d = d.slice(2);
      if (/^05\d{8}$/.test(d)) d = '966' + d.slice(1);
      else if (/^5\d{8}$/.test(d)) d = '966' + d;
      return d.length >= 9 ? d : '';
    },
    toast(title, variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: { title: title, text: text || '', icon: variant == 'success' ? 'CheckIcon' : 'AlertTriangleIcon', variant: variant || 'success' },
      });
    },
    // ------------------------------------------------------------ actions
    // every action: a reply → warnings[] shown on the page, the list + the sidebar badges again
    done(r, ok, name) {
      var g = this;
      var w = pick(r, 'warnings');
      if (Array.isArray(w) && w.length) {
        g.warnings = w.map(function (x) {
          var t = typeof x == 'string' ? x : (x && (x.message || x.text || x.title)) || '';
          return (name ? '«' + name + '»: ' : '') + t;
        }).filter(function (x) { return x; });
      }
      g.toast(ok);
      g.load(true);
      refreshRequestCounts();
    },
    post(path, body, ok, modal) {
      var g = this, row = g.cur;
      if (g.sending) return;
      g.sending = true;
      g.busyId = body.id;
      return reqPost(path, body).then(function (r) {
        g.sending = false;
        g.busyId = '';
        if (!r || r.status != 100) {
          alert(errorText(r), 200);
          return;
        }
        if (modal) g.$bvModal.hide(modal);
        g.done(r, ok, row && row.student_name);
      }).catch(function () {
        g.sending = false;
        g.busyId = '';
        alert('تعذّر الاتصال بالخادم', 200);
      });
    },
    openApprove(r) {
      var d = this.conf.approve_defaults || {};
      this.cur = r;
      this.form = {
        reply: this.conf.auto_note_approve || '',
        actions: this.type == 'leave'
          ? { call: !!d.call, leave_record: !!d.leave_record, teachers: !!d.teachers }
          : { visit_record: true },
        propose: false,
        proposed: { date: r.date || this.today, time: r.time || '' },
      };
      this.$bvModal.show('prq-approve');
    },
    approve() {
      var r = this.cur, f = this.form;
      if (!r) return;
      var body = { id: r._id, reply: f.reply.trim(), actions: Object.assign({}, f.actions) };
      if (this.type == 'visit' && f.propose) {
        if (!f.proposed.date) {
          alert('اختر التاريخ المقترح', 200);
          return;
        }
        body.proposed = { date: f.proposed.date, time: f.proposed.time || '' };
      }
      this.post('/approve', body, 'تمت الموافقة على ' + this.T.noun, 'prq-approve');
    },
    openReject(r) {
      this.cur = r;
      this.form = { reply: this.conf.auto_note_reject || '', actions: {}, propose: false, proposed: { date: '', time: '' } };
      this.$bvModal.show('prq-reject');
    },
    reject() {
      if (!this.cur) return;
      this.post('/reject', { id: this.cur._id, reply: this.form.reply.trim() }, 'تم رفض ' + this.T.noun, 'prq-reject');
    },
    act(r, action) {
      var ask = {
        call: 'مناداة ' + r.student_name + ' الآن في نظام المناداة؟',
        leave_record: 'تسجيل خروج (استئذان) ' + r.student_name + ' الآن؟',
        teachers: 'إرسال «إذن خروج من المدرسة» لمعلمي ' + r.student_name + '؟',
        visit_record: 'إضافة الزيارة إلى سجل زيارات أولياء الأمور؟',
        done: this.type == 'leave' ? 'إنهاء طلب الاستئذان؟' : 'تأكيد أن الزيارة تمت؟',
      }[action];
      if (ask && !confirm(ask)) return;
      var ok = {
        call: 'تمت مناداة الطالب', leave_record: 'تم تسجيل خروج الطالب', teachers: 'تم إبلاغ المعلمين',
        visit_record: 'أضيفت إلى سجل الزيارات', done: this.type == 'leave' ? 'تم إنهاء الطلب' : 'تم تسجيل الزيارة',
      }[action];
      this.cur = r;
      this.post('/act', { id: r._id, action: action }, ok);
    },
    toggleHistory(r) {
      this.$set(this.openHistory, r._id, !this.openHistory[r._id]);
    },
    // ------------------------------------------------------------ contact
    openContact(r) {
      this.cur = r;
      var school = (this.user && this.user.name) || '';
      var when = r.date ? ' بتاريخ ' + r.date + (r.time ? ' الساعة ' + this.time12(r.time) : '') : '';
      this.contactForm = {
        message: 'ولي أمر الطالب ' + (r.student_name || '') + '، بخصوص ' + this.T.noun + when + ':\n\n' + (school ? school : ''),
        note: '',
      };
      this.$bvModal.show('prq-contact');
    },
    // records the contact (status «تم التواصل» + history); tel: / wa.me open by themselves
    contacted(method, note) {
      var r = this.cur;
      if (!r) return;
      var p = this.parentOf(r).phone;
      if ((method == 'call' || method == 'whatsapp') && !p) return;
      var g = this;
      reqPost('/contact', { id: r._id, method: method, note: (note || '').trim().slice(0, 500) }).then(function (x) {
        if (!x || x.status != 100) {
          alert(errorText(x), 200);
          return;
        }
        if (method == 'other') g.$bvModal.hide('prq-contact');
        g.done(x, 'تم تسجيل التواصل', r.student_name);
      }).catch(function () {
        alert('تعذّر تسجيل التواصل', 200);
      });
    },
    sendMsg(method) {
      var g = this, r = g.cur;
      if (!r) return;
      var msg = g.contactForm.message.trim();
      if (!msg) {
        alert('اكتب نص الرسالة', 200);
        return;
      }
      var phones = [g.parentOf(r).phone].filter(function (x) { return x; });
      if (!phones.length && Array.isArray(r.phones)) phones = r.phones;
      if (!phones.length) {
        alert('لا يوجد رقم جوال لولي الأمر في الطلب', 200);
        return;
      }
      var arr = [{ phones: phones, message: msg }];
      var label = { whatsapp: 'واتس ذكي', xwa: 'واتس مباشر', wa: 'اداة المتصفح', meta: 'واتساب ميتا', sms: 'رسالة نصية', push: 'إشعار الجوال' }[method] || method;
      if (method == 'wa') {
        $("#sendmodalwaarray").val(JSON.stringify(arr));
        $("#sendmodalwatype").val("students");
        g.$bvModal.hide('prq-contact');
        $("#sendWAModal").click();
        g.contacted('message', label + ': ' + msg);
        return;
      }
      if (!confirm('متأكد من إرسال الرسالة عبر ' + label + '؟')) return;
      $.post(api + '/user/messages/send-arr', {
        jwt: g.user.jwt,
        arr: JSON.stringify(arr),
        method: method,
        type: 'students',
      }).then(function (x) {
        if (x.status != 100) {
          alert(x.response, 200);
          return;
        }
        if (method != 'push') alert('تم الارسال بنجاح', 100);
        g.$bvModal.hide('prq-contact');
        g.contacted('message', label + ': ' + msg);
      }).fail(function () {
        alert('حدث خطأ', 200);
      });
    },
  },
};
</script>

<style scoped>
.prq {
  padding-bottom: 24px;
}
.prq-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}
.prq-head-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-size: 22px;
  flex-shrink: 0;
  color: var(--sk-p, #1D4ED8);
  background: var(--sk-ps, #E8EEFD);
}
.prq-visit .prq-head-icon {
  color: #5B3FD6;
  background: #EEEAFD;
}
.prq-head-text {
  flex: 1;
  min-width: 220px;
}
.prq-head h4 {
  margin: 0 0 2px;
  font-weight: 700;
  color: var(--sk-ink, #0F1733);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.prq-head p {
  margin: 0;
  color: var(--sk-muted, #667085);
  font-size: 13px;
}
.prq-live {
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--sk-bads, #FEE4E2);
  color: var(--sk-badi, #B42318);
}
.prq-live .fa-circle {
  font-size: 8px;
  vertical-align: middle;
  animation: prq-pulse 1.4s infinite;
}
@keyframes prq-pulse {
  50% { opacity: .25; }
}
.prq-head-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.prq-head-actions .btn {
  border-radius: 12px;
  font-weight: 600;
}

.prq-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 14px;
  border-radius: 12px;
  margin-bottom: 12px;
  font-size: 13px;
}
.prq-note > .fa {
  font-size: 18px;
  margin-top: 1px;
}
.prq-note.off {
  background: var(--sk-soft2, #EEF1F6);
  color: var(--sk-text, #344054);
}
.prq-note.warn {
  background: var(--sk-was, #FEF0C7);
  color: var(--sk-wai, #B54708);
  border: 1px solid #FEDF89;
}
.prq-note-body {
  flex: 1;
}
.prq-note-body ul {
  margin: 4px 0 0;
  padding-inline-start: 18px;
}
.prq-note .close {
  color: inherit;
  opacity: .7;
}

/* tabs */
.prq-tabs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 12px;
  scrollbar-width: thin;
}
.prq-tab {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 13px;
  border-radius: 12px;
  border: 1px solid var(--sk-line, #D5DAE3);
  background: var(--sk-card, #fff);
  color: var(--sk-text, #344054);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}
.prq-tab .n {
  min-width: 22px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 11.5px;
  line-height: 20px;
  text-align: center;
  background: var(--sk-soft2, #EEF1F6);
  color: var(--sk-text, #344054);
}
.prq-tab.on {
  background: var(--sk-ps, #E8EEFD);
  border-color: var(--sk-p, #1D4ED8);
  color: var(--sk-p, #1D4ED8);
}
.prq-tab.on .n {
  background: var(--sk-p, #1D4ED8);
  color: #fff;
}
.prq-tab.t-pending .n {
  background: var(--sk-was, #FEF0C7);
  color: var(--sk-wai, #B54708);
}
.prq-tab.t-pending.on .n {
  background: var(--sk-wa, #DC6803);
  color: #fff;
}

/* filters */
.prq-filters {
  background: var(--sk-card, #fff);
  border: 1px solid var(--sk-line, #D5DAE3);
  border-radius: 16px;
  padding: 12px 14px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.f-dates,
.f-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
}
.f-field {
  min-width: 150px;
}
.f-field.grow {
  flex: 1;
  min-width: 220px;
}
.f-field label {
  font-size: 12px;
  color: var(--sk-muted, #667085);
  margin-bottom: 3px;
}
.f-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding-bottom: 6px;
}
.chip {
  border: 1px solid var(--sk-line, #D5DAE3);
  background: var(--sk-card, #fff);
  color: var(--sk-text, #344054);
  border-radius: 999px;
  padding: 3px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.chip:hover {
  border-color: var(--sk-p, #1D4ED8);
}
.chip.on {
  background: var(--sk-ps, #E8EEFD);
  border-color: var(--sk-p, #1D4ED8);
  color: var(--sk-p, #1D4ED8);
}

/* list */
.prq-empty {
  text-align: center;
  padding: 40px 16px;
  background: var(--sk-card, #fff);
  border: 1px dashed var(--sk-line, #D5DAE3);
  border-radius: 16px;
  color: var(--sk-muted, #667085);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.prq-empty .fa {
  font-size: 34px;
  color: var(--sk-faint, #98A2B3);
}
.prq-empty b {
  color: var(--sk-ink, #0F1733);
}
.prq-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 14px;
}
.prq-card {
  background: var(--sk-card, #fff);
  border: 1px solid var(--sk-line, #D5DAE3);
  border-radius: 16px;
  box-shadow: var(--sk-shadow, none);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: opacity .15s;
}
/* the status colour on the start side (LTR terms: postcss-rtl flips it, like the rest of the template) */
.prq-card,
.prq-card.st-x {
  border-left: 4px solid var(--sk-line, #D5DAE3);
}
.prq-card.busy {
  opacity: .6;
  pointer-events: none;
}
.prq-card.st-pending { border-left-color: var(--sk-wa, #DC6803); }
.prq-card.st-approved { border-left-color: var(--sk-ok, #079455); }
.prq-card.st-contacted { border-left-color: var(--sk-in, #0086C9); }
.prq-card.st-done { border-left-color: var(--sk-p, #1D4ED8); }
.prq-card.st-rejected { border-left-color: var(--sk-bad, #D92D20); }
.prq-card.st-cancelled { border-left-color: var(--sk-faint, #98A2B3); }
.prq-card > header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px 8px;
}
.prq-avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
  background: var(--sk-ps, #E8EEFD);
  color: var(--sk-p, #1D4ED8);
}
.prq-visit .prq-avatar {
  background: #EEEAFD;
  color: #5B3FD6;
}
.prq-who {
  flex: 1;
  min-width: 0;
}
.prq-who h5 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--sk-ink, #0F1733);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.prq-who small {
  color: var(--sk-muted, #667085);
  font-size: 12px;
}
.prq-chip {
  flex-shrink: 0;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
}
.c-pending { background: var(--sk-was, #FEF0C7); color: var(--sk-wai, #B54708); }
.c-approved { background: var(--sk-oks, #DCFAE6); color: var(--sk-oki, #067647); }
.c-contacted { background: var(--sk-ins, #E0F2FE); color: var(--sk-ini, #026AA2); }
.c-done { background: var(--sk-ps, #E8EEFD); color: var(--sk-pi, #1E3A8A); }
.c-rejected { background: var(--sk-bads, #FEE4E2); color: var(--sk-badi, #B42318); }
.c-cancelled { background: var(--sk-soft2, #EEF1F6); color: var(--sk-muted, #667085); }
.prq-body {
  padding: 0 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}
.prq-when {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--sk-text, #344054);
}
.prq-when .fa {
  color: var(--sk-muted, #667085);
}
.w-rel {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--sk-soft2, #EEF1F6);
}
.w-rel.today {
  background: var(--sk-bads, #FEE4E2);
  color: var(--sk-badi, #B42318);
}
.w-time b {
  color: var(--sk-ink, #0F1733);
}
.prq-reason {
  background: var(--sk-soft, #F6F7FB);
  border-radius: 10px;
  padding: 8px 10px;
}
.prq-reason .lbl {
  font-size: 11px;
  font-weight: 700;
  color: var(--sk-muted, #667085);
}
.prq-reason p {
  margin: 2px 0 0;
  font-size: 13.5px;
  color: var(--sk-ink, #0F1733);
  white-space: pre-wrap;
  word-break: break-word;
}
.prq-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 12.5px;
  color: var(--sk-text, #344054);
}
.prq-meta .fa {
  color: var(--sk-muted, #667085);
  margin-inline-end: 2px;
}
.prq-meta a {
  color: var(--sk-p, #1D4ED8);
}
.muted {
  color: var(--sk-muted, #667085);
}
.ltr-num {
  direction: ltr;
  unicode-bidi: isolate;
  display: inline-block;
}
.prq-proposed,
.prq-reply {
  font-size: 12.5px;
  padding: 6px 10px;
  border-radius: 10px;
}
.prq-proposed {
  background: #EEEAFD;
  color: #4A2FBF;
}
.prq-reply {
  background: var(--sk-ins, #E0F2FE);
  color: var(--sk-ini, #026AA2);
}
.prq-done {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.done-chip {
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--sk-oks, #DCFAE6);
  color: var(--sk-oki, #067647);
}
.prq-card > footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-top: 1px solid var(--sk-line2, #E6E9EF);
  background: var(--sk-soft, #F6F7FB);
}
.prq-card > footer .btn {
  border-radius: 10px;
  font-weight: 600;
}
.prq-hist-btn {
  margin-inline-start: auto;
  color: var(--sk-muted, #667085);
}
.prq-history {
  padding: 10px 14px 12px;
  border-top: 1px dashed var(--sk-line, #D5DAE3);
}
.prq-history ol {
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
}
.prq-history li {
  position: relative;
  display: flex;
  gap: 10px;
  padding: 0 0 10px;
  font-size: 12.5px;
}
.prq-history li:not(:last-child)::before {
  content: '';
  position: absolute;
  top: 12px;
  bottom: -2px;
  inset-inline-start: 5px;
  width: 2px;
  background: var(--sk-line2, #E6E9EF);
}
.prq-history .dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 3px;
  flex-shrink: 0;
  background: var(--sk-p, #1D4ED8);
  box-shadow: 0 0 0 3px var(--sk-ps, #E8EEFD);
}
.prq-history li.by-parent .dot {
  background: #7A5AF8;
  box-shadow: 0 0 0 3px #EEEAFD;
}
.prq-history b {
  color: var(--sk-ink, #0F1733);
}
.prq-history small {
  display: block;
  color: var(--sk-muted, #667085);
}
.prq-history p {
  margin: 2px 0 0;
  white-space: pre-wrap;
}

/* modals */
.prq-sum {
  background: var(--sk-soft, #F6F7FB);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
  font-size: 13px;
}
.prq-sum b {
  font-size: 14.5px;
  color: var(--sk-ink, #0F1733);
}
.prq-sum small {
  color: var(--sk-muted, #667085);
}
.prq-sub {
  margin: 12px 0 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--sk-muted, #667085);
}
.prq-opt {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--sk-line, #D5DAE3);
  border-radius: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  font-size: 13.5px;
}
.prq-opt input {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: var(--sk-p, #1D4ED8);
}
.prq-opt b {
  display: block;
  color: var(--sk-ink, #0F1733);
}
.prq-opt small {
  color: var(--sk-muted, #667085);
  font-size: 12px;
}
.prq-opt.on {
  border-color: var(--sk-p, #1D4ED8);
  background: var(--sk-ps, #E8EEFD);
}
.prq-propose {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  padding: 0 4px 6px;
}
.prq-propose input {
  direction: ltr;
}
.prq-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}
.prq-modal-actions .btn {
  border-radius: 10px;
  font-weight: 600;
}
.prq-contact {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.prq-ct {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid var(--sk-line, #D5DAE3);
  text-decoration: none;
  color: var(--sk-ink, #0F1733);
}
.prq-ct .fa {
  font-size: 22px;
}
.prq-ct small {
  color: var(--sk-muted, #667085);
  font-size: 11.5px;
}
.prq-ct.call .fa { color: var(--sk-p, #1D4ED8); }
.prq-ct.wa .fa { color: #12B76A; }
.prq-ct.call:hover { background: var(--sk-ps, #E8EEFD); }
.prq-ct.wa:hover { background: var(--sk-oks, #DCFAE6); }
.prq-ct.disabled {
  opacity: .45;
  pointer-events: none;
}
.prq-send {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.prq-send .btn {
  border-radius: 10px;
  font-weight: 600;
}
.prq-other {
  display: flex;
  gap: 8px;
}

.prq-filter-btn {
  display: none;
}
@media (max-width: 991.98px) {
  .prq-list {
    grid-template-columns: 1fr;
  }
  .prq-filter-btn {
    display: inline-block;
  }
  .prq-filter-btn.on {
    border-color: var(--sk-p, #1D4ED8);
    color: var(--sk-p, #1D4ED8);
  }
  .prq-filters-closed {
    display: none;
  }
  .prq-head-actions {
    width: 100%;
  }
  .prq-head-actions .btn {
    flex: 1;
  }
  .f-field {
    flex: 1;
    min-width: 130px;
  }
  .prq-hist-btn {
    margin-inline-start: 0;
  }
}
</style>
